// ════════════════════════════════════════════════════════════
//  Effects.jsx — the anime half: sparks, shots, beams, scars
// ════════════════════════════════════════════════════════════
//  Every system here is a FIXED POOL driven by ONE useFrame. No
//  system ever mounts or unmounts a mesh mid-fight (React stays
//  silent while the battle runs — the IE real-time law), and none
//  adds a light: every glow is additive, emissive or basic.
//
//  The battle engine never calls in here. BattleScene reads the
//  engine's event stream and calls the `fx` bus; the systems below
//  register themselves on that bus when they mount.
//
//  ── POOL SIZES ARE DIALS ─────────────────────────────────────
//  If this ever costs frames, shrink these first. Never the look.
// ════════════════════════════════════════════════════════════
import { useMemo, useRef, useEffect, useState } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { glowTex, ringTex, telegraphTex, scorchTex, crackTex, grooveTex, smudgeTex, runeTex, textTex, sealBodyTex, sealFourTex } from "./fxTex.js";
import { sealOf } from "../data/seals.js";
import { mulberry32 } from "../ieBeingModels.jsx";

export const N_PARTICLES = 1400;
export const N_RINGS = 28;
export const N_SHOTS = 48;
export const N_ZONES = 32;
export const N_TEXTS = 28;
export const N_SCARS = 140;
export const N_SMUDGE = 40;   // footprints' own share of the scar pool
export const N_PILLARS = 6;

const ADD = THREE.AdditiveBlending;
const tmpColor = new THREE.Color();

// ════════════════════════════════════════════════════════════
//  PARTICLES — one Points object, a custom shader, 1400 slots
// ════════════════════════════════════════════════════════════
const P_VERT = `
uniform float uScale;
attribute float aSize; attribute float aAlpha; attribute vec3 aColor;
varying float vAlpha; varying vec3 vColor;
void main(){
  vAlpha = aAlpha; vColor = aColor;
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_PointSize = aSize * (uScale / -mv.z);
  gl_Position = projectionMatrix * mv;
}`;
const P_FRAG = `
uniform sampler2D map; varying float vAlpha; varying vec3 vColor;
void main(){
  vec4 t = texture2D(map, gl_PointCoord);
  gl_FragColor = vec4(vColor * t.rgb, t.a * vAlpha);
}`;

function Particles({ fx }) {
  const R = useMemo(() => mulberry32(808), []);
  const { size, viewport } = useThree();
  const data = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    const pos = new Float32Array(N_PARTICLES * 3);
    for (let i = 0; i < N_PARTICLES; i++) pos[i * 3 + 1] = -999;
    geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    geo.setAttribute("aSize", new THREE.BufferAttribute(new Float32Array(N_PARTICLES), 1));
    geo.setAttribute("aAlpha", new THREE.BufferAttribute(new Float32Array(N_PARTICLES), 1));
    geo.setAttribute("aColor", new THREE.BufferAttribute(new Float32Array(N_PARTICLES * 3), 3));
    const mat = new THREE.ShaderMaterial({
      uniforms: { map: { value: glowTex() }, uScale: { value: 300 } },
      vertexShader: P_VERT, fragmentShader: P_FRAG,
      transparent: true, depthWrite: false, blending: ADD,
    });
    const pts = new THREE.Points(geo, mat);
    pts.frustumCulled = false;
    const st = Array.from({ length: N_PARTICLES }, () => ({ life: 0, max: 1, vx: 0, vy: 0, vz: 0, g: 0, drag: 0, size: 1, r: 1, gg: 1, b: 1 }));
    return { geo, pts, st, next: 0 };
  }, []);

  useEffect(() => {
    // burst(x,y,z,color,n,speed,{rise,size,life,spread,up})
    fx.burst = (x, y, z, color, n = 16, speed = 6, o = {}) => {
      tmpColor.set(color);
      const P = data.geo.attributes.position.array;
      for (let k = 0; k < n; k++) {
        const i = data.next; data.next = (data.next + 1) % N_PARTICLES;
        const s = data.st[i];
        const a = R() * Math.PI * 2, el = (R() - 0.3) * (o.flat ? 0.4 : 1.6);
        const sp = speed * (0.35 + R() * 0.65);
        s.vx = Math.cos(a) * Math.cos(el) * sp; s.vz = Math.sin(a) * Math.cos(el) * sp;
        s.vy = Math.sin(el) * sp + (o.up || 0);
        if (o.dir) { s.vx += o.dir[0] * sp; s.vz += o.dir[1] * sp; }
        s.g = o.rise ? -(o.rise) : 9;
        s.drag = o.drag != null ? o.drag : 2.2;
        s.max = s.life = (o.life || 0.6) * (0.6 + R() * 0.7);
        s.size = (o.size || 0.5) * (0.6 + R() * 0.8);
        const white = o.white ? R() < o.white : false;
        s.r = white ? 1 : tmpColor.r; s.gg = white ? 1 : tmpColor.g; s.b = white ? 1 : tmpColor.b;
        const sx = o.spread || 0;
        P[i * 3] = x + (R() - 0.5) * sx; P[i * 3 + 1] = y + (R() - 0.5) * sx * 0.5; P[i * 3 + 2] = z + (R() - 0.5) * sx;
      }
    };
  }, [fx, data, R]);
  // sparks keep their size on any window: tuned at 720 px tall, dpr 1
  useEffect(() => { data.pts.material.uniforms.uScale.value = 300 * (size.height * viewport.dpr) / 720; }, [size.height, viewport.dpr, data]);
  useEffect(() => () => { data.geo.dispose(); data.pts.material.dispose(); }, [data]);

  useFrame((_, dtRaw) => {
    const dt = Math.min(dtRaw, 0.05) * (fx.timeScale ?? 1);
    const P = data.geo.attributes.position.array;
    const S = data.geo.attributes.aSize.array, A = data.geo.attributes.aAlpha.array, C = data.geo.attributes.aColor.array;
    for (let i = 0; i < N_PARTICLES; i++) {
      const s = data.st[i];
      if (s.life <= 0) { if (A[i] !== 0) { A[i] = 0; } continue; }
      s.life -= dt;
      const k = Math.exp(-s.drag * dt);
      s.vx *= k; s.vz *= k; s.vy = s.vy * k - s.g * dt;
      P[i * 3] += s.vx * dt; P[i * 3 + 1] += s.vy * dt; P[i * 3 + 2] += s.vz * dt;
      if (P[i * 3 + 1] < 0.03 && s.g > 0) { P[i * 3 + 1] = 0.03; s.vy *= -0.3; }
      const f = Math.max(0, s.life / s.max);
      A[i] = Math.min(1, f * 1.6);
      S[i] = s.size * (0.4 + f * 0.6);
      C[i * 3] = s.r; C[i * 3 + 1] = s.gg; C[i * 3 + 2] = s.b;
    }
    data.geo.attributes.position.needsUpdate = true;
    data.geo.attributes.aSize.needsUpdate = true;
    data.geo.attributes.aAlpha.needsUpdate = true;
    data.geo.attributes.aColor.needsUpdate = true;
  });
  return <primitive object={data.pts} />;
}

// ════════════════════════════════════════════════════════════
//  RINGS — shockwaves on the ground, and standing halos
// ════════════════════════════════════════════════════════════
function Rings({ fx }) {
  const refs = useRef([]);
  const st = useMemo(() => Array.from({ length: N_RINGS }, () => ({ t: 0, max: 1, r0: 0, r1: 1, vertical: false })), []);
  const next = useRef(0);
  const tex = useMemo(() => ringTex(), []);
  useEffect(() => {
    // ring(x,z,color,{r0,r1,life,y,vertical,yaw})
    fx.ring = (x, z, color, o = {}) => {
      const i = next.current; next.current = (i + 1) % N_RINGS;
      const m = refs.current[i]; if (!m) return;
      const s = st[i];
      s.t = s.max = o.life || 0.5; s.r0 = o.r0 || 0.2; s.r1 = o.r1 || 3;
      m.position.set(x, o.y || 0.06, z);
      m.rotation.set(o.vertical ? 0 : -Math.PI / 2, o.yaw || 0, 0);
      m.material.color.set(color);
      m.visible = true;
    };
  }, [fx, st]);
  useFrame((_, dtRaw) => {
    const dt = Math.min(dtRaw, 0.05) * (fx.timeScale ?? 1);
    for (let i = 0; i < N_RINGS; i++) {
      const m = refs.current[i], s = st[i];
      if (!m || !m.visible) continue;
      s.t -= dt;
      if (s.t <= 0) { m.visible = false; continue; }
      const f = 1 - s.t / s.max;
      const e = 1 - Math.pow(1 - f, 3);
      const r = s.r0 + (s.r1 - s.r0) * e;
      m.scale.set(r * 2, r * 2, 1);
      m.material.opacity = (1 - f) * 0.95;
    }
  });
  return Array.from({ length: N_RINGS }, (_, i) => (
    <mesh key={i} ref={(el) => (refs.current[i] = el)} visible={false} renderOrder={3}>
      <planeGeometry args={[1, 1]} />
      <meshBasicMaterial map={tex} transparent depthWrite={false} blending={ADD} toneMapped={false} side={THREE.DoubleSide} />
    </mesh>
  ));
}

// ════════════════════════════════════════════════════════════
//  SHOTS — the engine's projectiles, drawn from a pool keyed by id
// ════════════════════════════════════════════════════════════
const SHAPE_GEO = {
  orb: () => new THREE.SphereGeometry(1, 14, 10),
  star: () => new THREE.OctahedronGeometry(1, 0),
  slash: () => new THREE.TorusGeometry(1, 0.16, 6, 18, Math.PI * 0.9),
  coin: () => new THREE.CylinderGeometry(1, 1, 0.22, 18),
  die: () => new THREE.BoxGeometry(1.3, 1.3, 1.3),        // a die is honestly a cube
  scale: () => new THREE.ConeGeometry(0.7, 1.8, 5),
  void: () => new THREE.IcosahedronGeometry(1, 1),
  halo: () => new THREE.TorusGeometry(0.85, 0.22, 8, 20),
  ant: () => new THREE.SphereGeometry(1, 8, 6),
  dragonfly: () => new THREE.ConeGeometry(0.6, 2.0, 6),
  fang: () => new THREE.ConeGeometry(0.5, 2.2, 6),
};
function Shots({ battle, fx }) {
  const geos = useMemo(() => Object.fromEntries(Object.entries(SHAPE_GEO).map(([k, f]) => [k, f()])), []);
  useEffect(() => () => Object.values(geos).forEach((g) => g.dispose()), [geos]);
  const live = useMemo(() => new Set(), []);
  const glow = useMemo(() => glowTex(), []);
  const slots = useRef([]);         // { group, core, halo, trail, id }
  const byId = useRef(new Map());
  useFrame(({ clock }, dtRaw) => {
    const b = battle.current; if (!b) return;
    live.clear();
    for (const p of b.projectiles) {
      live.add(p.id);
      let s = byId.current.get(p.id);
      if (!s) {
        s = slots.current.find((x) => x && x.id == null);
        if (!s) continue;
        s.id = p.id; byId.current.set(p.id, s);
        s.core.geometry = geos[p.shape] || geos.orb;
        s.core.material.color.set(p.color);
        s.halo.material.color.set(p.color);
        s.trail.material.color.set(p.color);
        s.group.visible = true;
        s.spin = 0;
        s.lastTrail = 0;
      }
      const sp = Math.hypot(p.vx, p.vz) || 1;
      const yaw = Math.atan2(p.vx, p.vz);
      s.group.position.set(p.x, p.y || 0.9, p.z);
      s.group.rotation.set(0, yaw, 0);
      const r = p.r;
      s.core.scale.setScalar(r * (p.shape === "orb" || p.shape === "ant" ? 0.7 : 0.8));
      s.spin += Math.min(dtRaw, 0.05) * 15 * (fx.timeScale ?? 1);
      if (p.shape === "coin" || p.shape === "die" || p.shape === "star" || p.shape === "halo") s.core.rotation.set(s.spin, s.spin * 0.7, 0);
      else if (p.shape === "slash") s.core.rotation.set(Math.PI / 2, 0, Math.PI / 2 + Math.sin(s.spin) * 0.2);
      else s.core.rotation.set(Math.PI / 2, 0, 0);
      s.halo.scale.setScalar(r * 4.2 * (1 + Math.sin(clock.elapsedTime * 30 + p.id) * 0.08));
      // the streak behind it — stretched with speed
      const L = Math.min(3.5, sp * 0.12);
      s.trail.scale.set(r * 1.3, L, 1);
      s.trail.position.set(0, 0, -L / 2);
      // a few sparks shed along the way
      if (fx.burst && fx.timeScale !== 0 && clock.elapsedTime - s.lastTrail > 0.05) {
        s.lastTrail = clock.elapsedTime;
        fx.burst(p.x, p.y || 0.9, p.z, p.color, 1, 0.6, { life: 0.35, size: r * 1.6, rise: 0.5, drag: 4 });
      }
    }
    for (const [id, s] of byId.current) if (!live.has(id)) { s.id = null; s.group.visible = false; byId.current.delete(id); }
  });
  return Array.from({ length: N_SHOTS }, (_, i) => (
    <group key={i} visible={false} ref={(el) => {
      if (!el) return;
      slots.current[i] = slots.current[i] || {};
      Object.assign(slots.current[i], { group: el, core: el.children[0], halo: el.children[1], trail: el.children[2] });
    }}>
      <mesh><sphereGeometry args={[1, 12, 8]} /><meshBasicMaterial toneMapped={false} /></mesh>
      <sprite><spriteMaterial map={glow} transparent depthWrite={false} blending={ADD} toneMapped={false} /></sprite>
      <mesh rotation={[-Math.PI / 2, 0, 0]}><planeGeometry args={[1, 1]} /><meshBasicMaterial map={glow} transparent opacity={0.75} depthWrite={false} blending={ADD} toneMapped={false} side={THREE.DoubleSide} /></mesh>
    </group>
  ));
}

// ════════════════════════════════════════════════════════════
//  BEAMS — one per side, read straight off the fighter
// ════════════════════════════════════════════════════════════
function Beams({ battle, fx }) {
  const g = [useRef(), useRef()];
  const glow = useMemo(() => glowTex(), []);
  useFrame(({ clock }) => {
    const b = battle.current; if (!b) return;
    const t = clock.elapsedTime;
    for (let side = 0; side < 2; side++) {
      const grp = g[side].current; if (!grp) continue;
      const s = b.sides[side];
      const f = s.active >= 0 ? s.roster[s.active] : null;
      if (!f || !f.beam) { grp.visible = false; continue; }
      grp.visible = true;
      const L = f.beam.len, W = f.beam.width;
      grp.position.set(f.x, 0.85, f.z);
      grp.rotation.set(0, f.beam.yaw, 0);
      const [outer, core, flare, tip] = grp.children;
      const pulse = 1 + Math.sin(t * 40) * 0.1 + Math.sin(t * 13) * 0.06;
      outer.scale.set(W * 1.4 * pulse, L, W * 1.4 * pulse); outer.position.z = L / 2;
      core.scale.set(W * 0.45, L, W * 0.45); core.position.z = L / 2;
      flare.scale.setScalar(W * 4 * pulse);
      tip.position.z = L; tip.scale.setScalar(W * 3.2 * pulse);
      outer.material.color.set(f.beam.color); flare.material.color.set(f.beam.color); tip.material.color.set(f.beam.color);
      if (fx.burst && fx.timeScale !== 0 && Math.random() < 0.6) {   // eslint-disable-line -- visual-only spray; never feeds the sim
        const d = Math.random() * L;
        fx.burst(f.x + Math.sin(f.beam.yaw) * d, 0.85, f.z + Math.cos(f.beam.yaw) * d, f.beam.color, 1, 3, { life: 0.4, size: W * 1.2, rise: 2 });
      }
    }
  });
  const beam = (i) => (
    <group ref={g[i]} visible={false} key={i}>
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[1, 1, 1, 18, 1, true]} />
        <meshBasicMaterial transparent opacity={0.45} depthWrite={false} blending={ADD} toneMapped={false} side={THREE.DoubleSide} />
      </mesh>
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[1, 1, 1, 12, 1, true]} />
        <meshBasicMaterial color="#ffffff" transparent opacity={0.95} depthWrite={false} blending={ADD} toneMapped={false} />
      </mesh>
      <sprite><spriteMaterial map={glow} transparent depthWrite={false} blending={ADD} toneMapped={false} /></sprite>
      <sprite><spriteMaterial map={glow} transparent depthWrite={false} blending={ADD} toneMapped={false} /></sprite>
    </group>
  );
  return <>{beam(0)}{beam(1)}</>;
}

// ════════════════════════════════════════════════════════════
//  ZONES — the telegraph for every strike from above, and the
//  falling light that answers it
// ════════════════════════════════════════════════════════════
function Zones({ battle }) {
  const tele = useMemo(() => telegraphTex(), []);
  const glow = useMemo(() => glowTex(), []);
  const refs = useRef([]);
  const byId = useRef(new Map());
  const live = useMemo(() => new Set(), []);
  useFrame(({ clock, camera }) => {
    const b = battle.current; if (!b) return;
    live.clear();
    for (const z of b.zones) {
      live.add(z.id);
      let s = byId.current.get(z.id);
      if (!s) {
        s = refs.current.find((x) => x && x.id == null);
        if (!s) continue;
        s.id = z.id; byId.current.set(z.id, s);
        s.group.visible = true;
        s.disc.material.color.set(z.color);
        s.streak.material.color.set(z.color);
        // the incoming Art's own seal inside the warning, so you can read WHAT is coming
        const seal = sealOf(z.art);
        s.body.material.map = sealBodyTex(seal); s.body.material.color.set(z.color); s.body.material.needsUpdate = true;
        s.four.children[0].material.map = sealFourTex(seal); s.four.children[0].material.needsUpdate = true;
      }
      s.group.position.set(z.x, 0, z.z);
      const max = z.tMax || 0.35;
      if (!z.done) {
        const f = 1 - Math.max(0, z.t) / max;          // 0 → 1 as impact nears
        s.disc.visible = !z.nova;
        s.disc.scale.setScalar(z.r * 2 * (1.25 - f * 0.25));
        s.disc.material.opacity = 0.25 + f * 0.6 + Math.sin(clock.elapsedTime * 30) * 0.08;
        s.disc.rotation.z = clock.elapsedTime * 1.5;
        s.body.visible = s.four.visible = !z.nova;
        s.body.scale.setScalar(z.r * 1.7); s.four.scale.setScalar(z.r * 1.7);
        s.body.material.opacity = s.four.children[0].material.opacity = 0.35 + f * 0.55;
        s.body.rotation.z = -clock.elapsedTime * 1.1;
        s.four.rotation.y = Math.atan2(camera.position.x - z.x, camera.position.z - z.z);   // Khao at the top of your screen
        // the light falls in the last third
        s.streak.visible = !z.nova && f > 0.66;
        const k = (f - 0.66) / 0.34;
        s.streak.scale.set(z.r * 0.7, 9 * (1 - k * 0.9) + 0.5, 1);
        s.streak.position.y = 4.5 * (1 - k) + 0.5;
      } else {
        s.disc.visible = false; s.streak.visible = false; s.body.visible = false; s.four.visible = false;
      }
    }
    for (const [id, s] of byId.current) if (!live.has(id)) { s.id = null; s.group.visible = false; byId.current.delete(id); }
  });
  return Array.from({ length: N_ZONES }, (_, i) => (
    <group key={i} visible={false} ref={(el) => {
      if (!el) return;
      refs.current[i] = refs.current[i] || {};
      Object.assign(refs.current[i], { group: el, disc: el.children[0], streak: el.children[1], body: el.children[2], four: el.children[3] });
    }}>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.05, 0]} renderOrder={2}>
        <planeGeometry args={[1, 1]} />
        <meshBasicMaterial map={tele} transparent depthWrite={false} blending={ADD} toneMapped={false} />
      </mesh>
      <sprite><spriteMaterial map={glow} transparent depthWrite={false} blending={ADD} toneMapped={false} /></sprite>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.06, 0]} renderOrder={2}>
        <planeGeometry args={[1, 1]} />
        <meshBasicMaterial transparent depthWrite={false} blending={ADD} toneMapped={false} />
      </mesh>
      <group position={[0, 0.065, 0]}>
        <mesh rotation={[-Math.PI / 2, 0, 0]} renderOrder={2}>
          <planeGeometry args={[1, 1]} />
          <meshBasicMaterial transparent depthWrite={false} blending={ADD} toneMapped={false} />
        </mesh>
      </group>
    </group>
  ));
}

// ════════════════════════════════════════════════════════════
//  WORDS — CLASH!, damage, statuses — painted sprites that pop
// ════════════════════════════════════════════════════════════
function Texts({ fx }) {
  // (dt below is scaled by fx.timeScale so pause freezes the words too)
  const refs = useRef([]);
  const st = useMemo(() => Array.from({ length: N_TEXTS }, () => ({ t: 0, max: 1, vy: 0, s: 1, big: false })), []);
  const next = useRef(0);
  useEffect(() => {
    fx.text = (x, y, z, text, color = "#fff", o = {}) => {
      const i = next.current; next.current = (i + 1) % N_TEXTS;
      const sp = refs.current[i]; if (!sp) return;
      const { tex, aspect } = textTex(String(text), color, !!o.big);
      sp.material.map = tex; sp.material.needsUpdate = true;
      const s = st[i];
      s.t = s.max = o.life || (o.big ? 1.3 : 0.9);
      s.s = (o.big ? 0.78 : o.small ? 0.3 : 0.46) * (o.scale || 1);
      s.aspect = aspect; s.vy = o.big ? 0.6 : 1.8;
      s.jx = (Math.random() - 0.5) * 0.6;   // eslint-disable-line -- visual-only jitter
      sp.position.set(x + s.jx, y, z);
      sp.visible = true;
    };
  }, [fx, st]);
  useFrame((_, dtRaw) => {
    const dt = Math.min(dtRaw, 0.05) * (fx.timeScale === 0 ? 0 : 1);
    for (let i = 0; i < N_TEXTS; i++) {
      const sp = refs.current[i], s = st[i];
      if (!sp || !sp.visible) continue;
      s.t -= dt;
      if (s.t <= 0) { sp.visible = false; continue; }
      const f = 1 - s.t / s.max;
      // pop in big, settle, float, fade
      const pop = f < 0.12 ? 1.6 - (f / 0.12) * 0.6 : 1;
      sp.scale.set(s.s * s.aspect * pop, s.s * pop, 1);
      sp.position.y += s.vy * dt * (1 - f);
      sp.material.opacity = f > 0.7 ? (1 - f) / 0.3 : 1;
    }
  });
  return Array.from({ length: N_TEXTS }, (_, i) => (
    <sprite key={i} ref={(el) => (refs.current[i] = el)} visible={false} renderOrder={10}>
      <spriteMaterial transparent depthTest={false} depthWrite={false} toneMapped={false} />
    </sprite>
  ));
}

// ════════════════════════════════════════════════════════════
//  SCARS — the arena remembers the fight
// ════════════════════════════════════════════════════════════
//  Scorches, craters, beam furrows, footprints and the Ascent rune.
//  They fade slowly (a fight's history stays readable for a minute)
//  and the oldest is recycled when the pool is full.
const SCAR_LIFE = { smudge: 9, scorch: 55, crater: 75, groove: 55, rune: 40 };
function Scars({ fx }) {
  const refs = useRef([]);
  const st = useMemo(() => Array.from({ length: N_SCARS }, () => ({ t: 0, max: 1, kind: "scorch", glow: 0, peak: 1 })), []);
  const next = useRef(0);
  const smudgeNext = useRef(0);
  const tex = useMemo(() => ({
    scorch: [scorchTex(0), scorchTex(1), scorchTex(2)],
    crater: [crackTex(0), crackTex(1), crackTex(2)],
    groove: [grooveTex()], smudge: [smudgeTex()], rune: [runeTex()],
  }), []);
  const y = useRef(0);
  useEffect(() => {
    // scar(kind, x, z, {r, yaw, len, color})
    fx.scar = (kind, x, z, o = {}) => {
      // footprints live in their own short ring (the first N_SMUDGE
      // slots) so they never overwrite a crater, rune or furrow
      let i;
      if (kind === "smudge") { i = smudgeNext.current; smudgeNext.current = (i + 1) % N_SMUDGE; }
      else { i = N_SMUDGE + next.current; next.current = (next.current + 1) % (N_SCARS - N_SMUDGE); }
      const m = refs.current[i]; if (!m) return;
      const s = st[i];
      const list = tex[kind] || tex.scorch;
      m.material.map = list[(i * 7) % list.length]; m.material.needsUpdate = true;
      s.t = s.max = SCAR_LIFE[kind] || 40; s.kind = kind;
      // crater/rune seams glow in the hit's colour, then cool
      s.glow = kind === "crater" || kind === "rune" || kind === "groove" ? 1 : 0;
      m.material.color.set(s.glow ? (o.color || "#ffffff") : "#ffffff");
      m.material.blending = kind === "rune" ? ADD : THREE.NormalBlending;
      s.peak = kind === "rune" ? 0.45 : kind === "crater" ? 0.8 : 1;
      // stack heights so scars never z-fight each other
      y.current = (y.current + 1) % 40;
      m.position.set(x, 0.012 + y.current * 0.0006, z);
      m.rotation.set(-Math.PI / 2, 0, o.yaw != null ? -o.yaw + Math.PI / 2 : (i * 2.39) % 6.28);
      if (kind === "groove") m.scale.set(o.len || 6, (o.r || 1) * 1.2, 1);
      else m.scale.set((o.r || 1) * 2, (o.r || 1) * 2, 1);
      m.visible = true;
    };
  }, [fx, st, tex]);
  useFrame((_, dtRaw) => {
    const dt = Math.min(dtRaw, 0.05) * (fx.timeScale === 0 ? 0 : 1);
    for (let i = 0; i < N_SCARS; i++) {
      const m = refs.current[i], s = st[i];
      if (!m || !m.visible) continue;
      s.t -= dt;
      if (s.t <= 0) { m.visible = false; continue; }
      const f = s.t / s.max;
      m.material.opacity = Math.min(1, f * 3) * (s.kind === "smudge" ? 0.6 : s.peak || 1);
      if (s.glow > 0) {
        s.glow = Math.max(0, s.glow - dt * 0.35);
        // cooling: from the hit's colour toward ash
        m.material.color.lerp(tmpColor.set("#3a2c48"), dt * 0.4);
      }
    }
  });
  return Array.from({ length: N_SCARS }, (_, i) => (
    <mesh key={i} ref={(el) => (refs.current[i] = el)} visible={false} renderOrder={1}>
      <planeGeometry args={[1, 1]} />
      <meshBasicMaterial transparent depthWrite={false} polygonOffset polygonOffsetFactor={-2} toneMapped={false} />
    </mesh>
  ));
}

// ════════════════════════════════════════════════════════════
//  PILLARS — the column of light for an Ascent or a U'hlti tell
// ════════════════════════════════════════════════════════════
function Pillars({ fx }) {
  const refs = useRef([]);
  const st = useMemo(() => Array.from({ length: N_PILLARS }, () => ({ t: 0, max: 1, follow: null, w: 1 })), []);
  const next = useRef(0);
  const glow = useMemo(() => glowTex(), []);
  const { camera } = useThree();
  useEffect(() => {
    fx.pillar = (x, z, color, o = {}) => {
      const i = next.current; next.current = (i + 1) % N_PILLARS;
      const m = refs.current[i]; if (!m) return;
      const s = st[i];
      s.t = s.max = o.life || 2; s.w = o.w || 1.6; s.follow = o.follow || null;
      m.position.set(x, 0, z);
      m.children[0].material.color.set(color);
      m.children[1].material.color.set(color);
      m.visible = true;
    };
  }, [fx, st]);
  useFrame(({ clock }, dtRaw) => {
    const dt = Math.min(dtRaw, 0.05) * (fx.timeScale === 0 ? 0 : 1);
    for (let i = 0; i < N_PILLARS; i++) {
      const m = refs.current[i], s = st[i];
      if (!m || !m.visible) continue;
      s.t -= dt;
      if (s.t <= 0) { m.visible = false; continue; }
      if (s.follow) { const p = s.follow(); if (p) m.position.set(p.x, 0, p.z); }
      const f = 1 - s.t / s.max;
      const env = f < 0.15 ? f / 0.15 : f > 0.8 ? (1 - f) / 0.2 : 1;
      // billboard on Y only — a column that tips stops being a column
      m.rotation.y = Math.atan2(camera.position.x - m.position.x, camera.position.z - m.position.z);
      const wob = 1 + Math.sin(clock.elapsedTime * 22) * 0.06;
      m.children[0].scale.set(s.w * env * wob, 30, 1);
      m.children[0].material.opacity = 0.55 * env;
      m.children[1].scale.set(s.w * 0.35 * env, 30, 1);
      m.children[1].material.opacity = 0.9 * env;
    }
  });
  return Array.from({ length: N_PILLARS }, (_, i) => (
    <group key={i} ref={(el) => (refs.current[i] = el)} visible={false}>
      <mesh position={[0, 15, 0]}><planeGeometry args={[1, 1]} /><meshBasicMaterial map={glow} transparent depthWrite={false} blending={ADD} toneMapped={false} side={THREE.DoubleSide} /></mesh>
      <mesh position={[0, 15, 0]}><planeGeometry args={[1, 1]} /><meshBasicMaterial map={glow} transparent depthWrite={false} blending={ADD} toneMapped={false} side={THREE.DoubleSide} /></mesh>
    </group>
  ));
}

// ════════════════════════════════════════════════════════════
//  CONSTRUCTS — the Tomes that stand on the field (Bhodi Tree, Chao
//  Garden, Anima House, Chao D'uhmdumn, The Stars & Skies). Simple
//  primitive sculptures in their caster's colour; a fixed pool, keyed
//  by the engine's construct id.
// ════════════════════════════════════════════════════════════
export const N_CONSTRUCTS = 8;
// what each Tome GIVES glows in that Aenergy's canon colour (Anima gold, Khao violet)
const ANIMA_C = "#F2B134", ANIMA_G = "#FFDC8A", KHAO_C = "#A855F7", KHAO_G = "#DDB8FF";
function ConstructBody({ kind }) {
  // one silhouette per Tome, so you can read the field at a glance
  if (kind === "tree") return (                 // Bhodi Tree: a little tree hung with Anima fruit
    <group>
      <mesh position={[0, 0.45, 0]}><cylinderGeometry args={[0.09, 0.14, 0.9, 7]} /><meshStandardMaterial color="#4a3426" roughness={0.9} /></mesh>
      <mesh position={[0, 1.05, 0]}><icosahedronGeometry args={[0.5, 0]} /><meshStandardMaterial color="#3f8a5a" emissive="#2f6" emissiveIntensity={0.15} roughness={0.8} flatShading /></mesh>
      {[0, 1, 2].map((i) => <mesh key={i} position={[Math.cos(i * 2.1) * 0.42, 0.88 + (i % 2) * 0.22, Math.sin(i * 2.1) * 0.42]}><sphereGeometry args={[0.085, 10, 8]} /><meshBasicMaterial color={ANIMA_G} toneMapped={false} /></mesh>)}
    </group>);
  if (kind === "garden") return (               // Chao Garden: Khao crystals growing out of the floor
    <group>
      {[0, 1, 2, 3, 4].map((i) => <mesh key={i} position={[Math.cos(i * 1.3) * 0.3, 0.25 + (i % 2) * 0.1, Math.sin(i * 1.3) * 0.3]} rotation={[0.2 * i, i, 0.1]}><octahedronGeometry args={[0.16 + (i % 3) * 0.05, 0]} /><meshStandardMaterial color={KHAO_C} emissive={KHAO_G} emissiveIntensity={0.7} flatShading /></mesh>)}
    </group>);
  if (kind === "house") return (                // Anima House: a little house with an Anima-gold roof
    <group>
      <mesh position={[0, 0.35, 0]}><cylinderGeometry args={[0.45, 0.5, 0.7, 6]} /><meshStandardMaterial color="#5a3a4a" roughness={0.8} /></mesh>
      <mesh position={[0, 0.9, 0]}><coneGeometry args={[0.6, 0.5, 6]} /><meshStandardMaterial color={ANIMA_C} emissive={ANIMA_C} emissiveIntensity={0.35} /></mesh>
      <mesh position={[0, 0.32, 0.47]}><planeGeometry args={[0.2, 0.3]} /><meshBasicMaterial color={ANIMA_G} toneMapped={false} /></mesh>
    </group>);
  if (kind === "duhmdumn") return (
    <group>
      {[0, 1, 2].map((i) => <mesh key={i} position={[0, 0.3 + i * 0.5, 0]}><sphereGeometry args={[0.42 - i * 0.08, 12, 10]} /><meshStandardMaterial color={i === 2 ? "#e8d8a8" : "#8a6a4a"} roughness={0.85} /></mesh>)}
      <mesh position={[0.12, 1.36, 0.28]}><sphereGeometry args={[0.06, 8, 6]} /><meshBasicMaterial color="#111" /></mesh>
      <mesh position={[-0.12, 1.36, 0.28]}><sphereGeometry args={[0.06, 8, 6]} /><meshBasicMaterial color="#111" /></mesh>
    </group>);
  // The Stars & Skies: a crescent moon with a ring of stars turning round it
  // (was a lone yellow diamond nobody could read — caP'n 2026-10-20 "that cube no idea what it is")
  return (
    <group position={[0, 1.25, 0]}>
      <mesh rotation={[0, 0, 0.9]}><torusGeometry args={[0.32, 0.085, 10, 32, Math.PI * 1.3]} /><meshBasicMaterial color={ANIMA_G} toneMapped={false} /></mesh>
      <group rotation={[0.45, 0, 0.2]}>
        <mesh rotation={[Math.PI / 2, 0, 0]}><torusGeometry args={[0.78, 0.018, 6, 72]} /><meshBasicMaterial color={ANIMA_C} transparent opacity={0.8} toneMapped={false} /></mesh>
        {[0, 1, 2, 3, 4].map((i) => { const t = i / 5 * Math.PI * 2; return (
          <mesh key={i} position={[Math.cos(t) * 0.78, 0, Math.sin(t) * 0.78]} rotation={[0.4, t, 0.3]} scale={i % 2 ? 0.7 : 1}>
            <octahedronGeometry args={[0.15, 0]} /><meshBasicMaterial color={i % 2 ? "#ffffff" : ANIMA_G} toneMapped={false} />
          </mesh>); })}
      </group>
    </group>);
}
function Constructs({ battle }) {
  const [list, setList] = useState([]);
  const refs = useRef({});
  // the roster of standing constructs changes rarely — React hears
  // only about arrivals and departures, never about the frame loop
  useFrame(({ clock, camera }) => {
    const b = battle.current; if (!b) return;
    const ids = b.constructs.map((k) => k.id).join(",");
    if (ids !== refs.current.__ids) { refs.current.__ids = ids; setList(b.constructs.map((k) => ({ id: k.id, kind: k.kind, art: k.art, x: k.x, z: k.z, color: k.color }))); }
    for (const k of b.constructs) {
      const g = refs.current[k.id]; if (!g) continue;
      g.rotation.y = clock.elapsedTime * 0.4;
      g.position.y = k.kind === "stars" ? Math.sin(clock.elapsedTime * 1.5) * 0.15 : 0;
      g.visible = !(k.intangibleT > 0 && Math.floor(clock.elapsedTime * 10) % 2);
      // it stands on its Tome's seal: the rim turns slowly, the Four face you
      const sl = refs.current["s" + k.id];
      if (sl) { sl.children[0].rotation.z = -clock.elapsedTime * 0.35; sl.children[1].rotation.y = Math.atan2(camera.position.x - k.x, camera.position.z - k.z); }
    }
  });
  return list.slice(0, N_CONSTRUCTS).map((k) => {
    const seal = sealOf(k.art);
    return (
      <group key={k.id} position={[k.x, 0, k.z]}>
        <group position={[0, 0.03, 0]} ref={(el) => { if (el) refs.current["s" + k.id] = el; else delete refs.current["s" + k.id]; }}>
          <mesh rotation={[-Math.PI / 2, 0, 0]} renderOrder={2}><planeGeometry args={[2.3, 2.3]} /><meshBasicMaterial map={sealBodyTex(seal)} color={k.color} transparent opacity={0.75} depthWrite={false} blending={ADD} toneMapped={false} /></mesh>
          <group position={[0, 0.004, 0]}><mesh rotation={[-Math.PI / 2, 0, 0]} renderOrder={2}><planeGeometry args={[2.3, 2.3]} /><meshBasicMaterial map={sealFourTex(seal)} transparent depthWrite={false} blending={ADD} toneMapped={false} /></mesh></group>
        </group>
        <group ref={(el) => { if (el) refs.current[k.id] = el; else delete refs.current[k.id]; }}><ConstructBody kind={k.kind} /></group>
      </group>
    );
  });
}

export default function Effects({ battle, fx }) {
  return (
    <group>
      <Scars fx={fx} />
      <Constructs battle={battle} />
      <Zones battle={battle} />
      <Rings fx={fx} />
      <Shots battle={battle} fx={fx} />
      <Beams battle={battle} fx={fx} />
      <Pillars fx={fx} />
      <Particles fx={fx} />
      <Texts fx={fx} />
    </group>
  );
}
